/**
 * Crew bookings: what each person on a wedding must capture, the agreement
 * they sign, and the messages Jena sends them (docs/principles.md 1.1-1.4).
 *
 * The agreement text is built here and frozen on the seat when it is sent, so
 * what the crew member signs is exactly what was sent, word for word.
 */

import {
  formatDateLong,
  formatTime,
  type IsoDate,
  type IsoTime,
} from "./dates";
import { formatMoney } from "./format";
import { TEAM_ROLE_LABEL, type TeamRole } from "./jobs";

const HANDOVER = "All original files handed over to Jenalee within 48 hours";

/** The must-haves for each role, offered when a seat is first filled. */
export const DEFAULT_DELIVERABLES: Record<TeamRole, string[]> = {
  lead_photographer: [
    "Full coverage of the day as per the timeline",
    "Family photos from the couple’s list",
    "The couple session",
    HANDOVER,
  ],
  lead_videographer: [
    "Getting ready",
    "The ceremony in full, start to finish, with clean audio",
    "All speeches in full, start to finish, with clean audio",
    "The first dance in full",
    "The couple session and details",
    "Reception until the agreed end time",
    `${HANDOVER} (every clip and audio file)`,
  ],
  second_photographer: [
    "Groom and groomsmen getting ready",
    "Second angle and guest reactions during the ceremony",
    "Guests, venue and details while the lead is with the couple",
    "Speeches and first dance from a second angle",
    HANDOVER,
  ],
  second_videographer: [
    "Groom and groomsmen getting ready",
    "Second angle of the ceremony, in full",
    "Second angle of the speeches and first dance, in full",
    `${HANDOVER} (every clip and audio file)`,
  ],
  content_creator: [
    "Phone content as briefed by Jenalee",
    "All files handed over by the end of the night",
  ],
  assistant: ["Help the lead with gear, family groupings and the dress"],
};

export interface AgreementDetails {
  businessName: string;
  jobName: string;
  eventDate: IsoDate;
  venue: string | null;
  callTime: IsoTime | null;
  role: TeamRole;
  personName: string;
  personEmail: string | null;
  personPhone: string | null;
  fee: number | null;
  travelFee: number | null;
  deliverables: string[];
  brief: string | null;
  terms: string;
}

/** The full agreement as sent and signed. Plain text, so it reads the same everywhere. */
export function buildAgreement(d: AgreementDetails): string {
  const lines = [
    `CREW AGREEMENT`,
    ``,
    `Between ${d.businessName} (Jenalee de Leeuw), and ${d.personName}${contact(d)}.`,
    ``,
    `THE BOOKING`,
    `Wedding: ${d.jobName}`,
    `Date: ${formatDateLong(d.eventDate)}`,
    `Venue: ${d.venue?.trim() || "to be confirmed in the game plan"}`,
    `Call time: ${d.callTime ? formatTime(d.callTime) : "to be confirmed in the game plan"}`,
    `Role: ${TEAM_ROLE_LABEL[d.role]}`,
    `Fee: ${d.fee !== null ? formatMoney(d.fee) : "as agreed"}`,
    `Travel: ${d.travelFee ? formatMoney(d.travelFee) : "included in the fee"}`,
    ``,
    `WHAT YOU CAPTURE AND DELIVER`,
    ...d.deliverables
      .filter((item) => item.trim())
      .map((item) => `- ${item.trim()}`),
  ];
  if (d.brief?.trim()) lines.push(``, `ALSO NOTE`, d.brief.trim());
  lines.push(``, `TERMS`, d.terms.trim());
  return lines.join("\n");
}

function contact(d: AgreementDetails): string {
  const parts = [d.personEmail, d.personPhone].filter(Boolean);
  return parts.length ? ` (${parts.join(", ")})` : "";
}

/** 32 random bytes as URL-safe base64: the secret part of /crew/<token>. */
export function newAgreementToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}

export function agreementMessage(
  firstName: string,
  jobName: string,
  eventDate: IsoDate,
  link: string,
): string {
  return [
    `Hi ${firstName}!`,
    ``,
    `Thank you for shooting ${jobName}'s wedding with us on ${formatDateLong(eventDate)}.`,
    `Here is your booking: the details, what you capture and hand over, your fee, and the terms. Please read it and sign at the bottom:`,
    link,
    ``,
    `Once you've signed, the booking stands until one of us releases it in writing. If anything changes on your side, please tell me straight away.`,
    ``,
    `Jenalee`,
  ].join("\n");
}

/** Who signs crew agreements for the studio; the agreement names her too. */
export const STUDIO_SIGNATORY = "Jenalee de Leeuw";

/** After Jena countersigns: their booking page now has the copy with both signatures. */
export function signedCopyMessage(
  firstName: string,
  jobName: string,
  eventDate: IsoDate,
  link: string,
): string {
  return [
    `Hi ${firstName}!`,
    ``,
    `I've signed your agreement for ${jobName}'s wedding on ${formatDateLong(eventDate)} too, so it's signed by both of us.`,
    `You can download your copy (a PDF with both signatures) from your booking page:`,
    link,
    ``,
    `Thank you, I'm looking forward to it!`,
    ``,
    `Jenalee`,
  ].join("\n");
}

export function releaseMessage(
  firstName: string,
  jobName: string,
  eventDate: IsoDate,
  reason: string,
): string {
  return [
    `Hi ${firstName},`,
    ``,
    `This is to confirm in writing that you are released from your booking for ${jobName}'s wedding on ${formatDateLong(eventDate)}.`,
    reason.trim() ? `Reason: ${reason.trim()}` : "",
    ``,
    `Thank you,`,
    `Jenalee`,
  ]
    .filter((line, index, all) => !(line === "" && all[index - 1] === ""))
    .join("\n");
}

export function withdrawalAcknowledgement(
  firstName: string,
  jobName: string,
  eventDate: IsoDate,
): string {
  return [
    `Hi ${firstName},`,
    ``,
    `I've received your withdrawal from ${jobName}'s wedding on ${formatDateLong(eventDate)} and noted it today. Please remember the terms you signed (section 2) on late notice.`,
    ``,
    `Jenalee`,
  ].join("\n");
}

export type BookingState =
  "empty" | "self" | "draft" | "sent" | "signed" | "released";

/** How many days out a seat without a signed agreement turns amber. */
export const SIGN_BY_DAYS = 30;

/** Lead seats are associate shoots; everything else is a second seat. */
export function isLeadRole(role: TeamRole): boolean {
  return role === "lead_photographer" || role === "lead_videographer";
}

/** The fee to offer when someone is put on a seat: their rate for that kind of seat. */
export function rateForRole(
  member: { lead_rate: number | null; second_rate: number | null },
  role: TeamRole,
): number | null {
  return isLeadRole(role) ? member.lead_rate : member.second_rate;
}

export interface CrewSeatSummary {
  status: "draft" | "sent" | "signed" | "released";
  event_date: IsoDate;
  fee: number | null;
  travel_fee: number | null;
  rating: number | null;
  /** Sum of the payments linked to this seat. */
  paid: number;
}

export interface CrewStats {
  weddings: number;
  upcoming: number;
  lastWedding: IsoDate | null;
  averageRating: number | null;
  ratedCount: number;
  /** Fee and travel still unpaid on weddings that have happened. */
  owed: number;
  paidTotal: number;
}

/**
 * One person's record across every wedding. A released seat is not counted
 * as a wedding shot (they didn't shoot it) and is never owed.
 */
export function crewStats(
  seats: CrewSeatSummary[],
  unlinkedPaid: number,
  on: IsoDate,
): CrewStats {
  const shot = seats.filter((seat) => seat.status !== "released");
  const past = shot.filter((seat) => seat.event_date <= on);
  const rated = shot.filter((seat) => seat.rating !== null);
  const owed = past.reduce((sum, seat) => {
    if (seat.fee === null) return sum;
    return sum + Math.max(0, seat.fee + (seat.travel_fee ?? 0) - seat.paid);
  }, 0);
  return {
    weddings: past.length,
    upcoming: shot.length - past.length,
    lastWedding:
      past.map((seat) => seat.event_date).sort().at(-1) ?? null,
    averageRating: rated.length
      ? Math.round(
          (rated.reduce((sum, seat) => sum + (seat.rating ?? 0), 0) /
            rated.length) *
            10,
        ) / 10
      : null,
    ratedCount: rated.length,
    owed: Math.round(owed * 100) / 100,
    paidTotal:
      Math.round(
        (seats.reduce((sum, seat) => sum + seat.paid, 0) + unlinkedPaid) * 100,
      ) / 100,
  };
}

export const BOOK_AGAIN_LABEL = {
  yes: "Yes, book again",
  maybe: "Maybe",
  no: "No",
} as const;
export type BookAgain = keyof typeof BOOK_AGAIN_LABEL;
